import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { message, Avatar, Form } from 'antd';
import { useCanCheck, useUserMetadata } from '@/ability/helpers';
import { useCreateProject } from '@hooks/projects/useCreateProject';
import { PROJECT_CLIENT_TYPE } from '@/constants/projectClientType';
import { budgetFromFormValue } from '@hooks/projects/projectHelpers';
import { Button } from '@/components/ui/Button';
import StatusBadge from '@/components/ui/StatusBadge';
import Select from '@/components/ui/Select';
import {
    getSalesLeads,
    getSalesLeadById,
    createSalesLead,
    updateSalesLead,
    deleteSalesLead,
    patchLeadStage,
    patchLeadStatus,
    inviteClient,
} from '@services/sales.service';

const STAGE_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'proposal_sent', label: 'Proposal sent' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'closed_won', label: 'Closed won' },
    { value: 'closed_lost', label: 'Closed lost' },
];

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'disqualified', label: 'Disqualified' },
    { value: 'converted', label: 'Converted' },
    { value: 'lost', label: 'Lost' },
];

export const SALES_LEADS_QUERY_KEY = ['sales', 'leads'];
export const SALES_LEAD_DETAIL_QUERY_KEY = (id) => ['sales', 'leads', 'detail', id];

const STATUS_TAB = {
    ALL: 'all',
    ACTIVE: 'active',
    QUALIFIED: 'qualified',
    DISQUALIFIED: 'disqualified',
    CLOSED: 'closed',
};
const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

/**
 * Fetch single lead by id. Export for use in SalesLeadDetailPanel.
 */
export const useSalesLeadDetail = (leadId) =>
    useQuery({
        queryKey: SALES_LEAD_DETAIL_QUERY_KEY(leadId),
        queryFn: () => getSalesLeadById(leadId),
        enabled: !!leadId,
    });

/**
 * All sales leads list logic: data, filters, table config, panel state, permissions, handlers.
 * UI should only consume this hook and render.
 */
export const useSalesLeads = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { role } = useUserMetadata() || {};
    const isClientRole = String(role || '').toLowerCase() === 'client';
    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState(null);
    const [stageLoadingId, setStageLoadingId] = useState(null);
    const [statusLoadingId, setStatusLoadingId] = useState(null);
    const [inviteLoadingId, setInviteLoadingId] = useState(null);
    const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
    const [createProjectLead, setCreateProjectLead] = useState(null);
    const [createLeadModalOpen, setCreateLeadModalOpen] = useState(false);
    const [createLeadForm] = Form.useForm();

    const can = useCanCheck();
    const canView = can('sales.leads.view');
    const canCreate = can('sales.leads.create');
    const canUpdate = can('sales.leads.update');
    const canDelete = can('sales.leads.delete');

    const { data: response, isError: leadsError } = useQuery({
        queryKey: [...SALES_LEADS_QUERY_KEY, { page: 1, limit: 100 }],
        queryFn: () => getSalesLeads({ page: 1, limit: 100 }),
        retry: false,
        staleTime: 0,
        refetchOnMount: true,
    });

    const createProjectMutation = useCreateProject();

    const rawLeads = response?.data ?? [];
    const leads = useMemo(
        () => rawLeads.filter((l) => (l.source || '').toLowerCase() !== 'open_market'),
        [rawLeads],
    );
    const total = leads.length;

    const activeCount = useMemo(() => leads.filter((l) => (l.status || '').toLowerCase() === 'active').length, [leads]);
    const qualifiedCount = useMemo(
        () => leads.filter((l) => (l.status || '').toLowerCase() === 'qualified').length,
        [leads],
    );
    const closedCount = useMemo(
        () =>
            leads.filter((l) =>
                ['closed_won', 'closed_lost', 'closed'].includes((l.status || '').toLowerCase().replace(/\s/g, '_')),
            ).length,
        [leads],
    );

    const selectedLeadName = useMemo(
        () =>
            leads.find((l) => l.id === selectedLeadId)?.name ??
            leads.find((l) => l.id === selectedLeadId)?.email ??
            'Lead details',
        [leads, selectedLeadId],
    );

    const filteredLeads = useMemo(() => {
        let list = leads;
        const statusLower = (s) => (s || '').toLowerCase();
        if (statusTab === STATUS_TAB.ACTIVE) list = list.filter((l) => statusLower(l.status) === 'active');
        if (statusTab === STATUS_TAB.QUALIFIED) list = list.filter((l) => statusLower(l.status) === 'qualified');
        if (statusTab === STATUS_TAB.DISQUALIFIED) list = list.filter((l) => statusLower(l.status) === 'disqualified');
        if (statusTab === STATUS_TAB.CLOSED) {
            list = list.filter((l) =>
                ['closed_won', 'closed_lost', 'closed'].includes(statusLower(l.status).replace(/\s/g, '_')),
            );
        }
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(
                (l) =>
                    (l.name || '').toLowerCase().includes(q) ||
                    (l.email || '').toLowerCase().includes(q) ||
                    (l.company || '').toLowerCase().includes(q) ||
                    (l.phone || '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [leads, statusTab, searchText]);

    const tableData = useMemo(
        () =>
            filteredLeads.map((l) => ({
                key: l.id,
                id: l.id,
                name: l.name,
                email: l.email,
                phone: l.phone,
                company: l.company,
                source: l.source,
                stage: l.stage,
                status: l.status,
                isInvited: l.isInvited,
                clientId: l.clientId,
                projectId: l.projectId,
                tenantName: l.tenant?.name,
                assignedTo: l.assignedTo ?? l.assignedToUserId,
                notes: l.notes,
            })),
        [filteredLeads],
    );

    const createLeadMutation = useMutation({
        mutationFn: (body) => createSalesLead(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SALES_LEADS_QUERY_KEY });
        },
    });

    const updateLeadMutation = useMutation({
        mutationFn: ({ id, body }) => updateSalesLead(id, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SALES_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: SALES_LEAD_DETAIL_QUERY_KEY(selectedLeadId) });
        },
    });

    const deleteLeadMutation = useMutation({
        mutationFn: (id) => deleteSalesLead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SALES_LEADS_QUERY_KEY });
            setPanelOpen(false);
            setSelectedLeadId(null);
        },
    });

    const patchStageMutation = useMutation({
        mutationFn: ({ leadId, stage }) => patchLeadStage(leadId, stage),
        onMutate: ({ leadId }) => {
            setStageLoadingId(leadId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SALES_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: SALES_LEAD_DETAIL_QUERY_KEY(selectedLeadId) });
        },
        onError: (err) => {
            message.error(err?.response?.data?.message ?? 'Failed to update stage');
        },
        onSettled: () => {
            setStageLoadingId(null);
        },
    });

    const patchStatusMutation = useMutation({
        mutationFn: ({ leadId, status }) => patchLeadStatus(leadId, status),
        onMutate: ({ leadId }) => {
            setStatusLoadingId(leadId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SALES_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: SALES_LEAD_DETAIL_QUERY_KEY(selectedLeadId) });
        },
        onError: (err) => {
            message.error(err?.response?.data?.message ?? 'Failed to update status');
        },
        onSettled: () => {
            setStatusLoadingId(null);
        },
    });

    const inviteClientMutation = useMutation({
        mutationFn: (leadId) => inviteClient(leadId),
        onMutate: (leadId) => {
            setInviteLoadingId(leadId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SALES_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: SALES_LEAD_DETAIL_QUERY_KEY(selectedLeadId) });
            message.success('Client invite sent');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message ?? 'Failed to invite client');
        },
        onSettled: () => {
            setInviteLoadingId(null);
        },
    });

    const handleOpenCreateProjectModal = (lead) => {
        setCreateProjectLead(lead);
        setCreateProjectModalOpen(true);
    };

    const handleCloseCreateProjectModal = () => {
        setCreateProjectModalOpen(false);
        setCreateProjectLead(null);
    };

    const handleCreateProjectFromLead = (values) => {
        if (!createProjectLead) return;
        const base = {
            name:
                (values.name ?? '').trim() ||
                createProjectLead.name ||
                createProjectLead.company ||
                createProjectLead.email ||
                'Project',
            budget: budgetFromFormValue(values.budget),
            priority: values.priority,
        };

        const payload =
            createProjectLead.clientId != null
                ? {
                      ...base,
                      clientType: PROJECT_CLIENT_TYPE.MINE_AND_PLATFORM,
                      clientId: createProjectLead.clientId,
                  }
                : {
                      ...base,
                      clientType: PROJECT_CLIENT_TYPE.OUT_OF_PLATFORM,
                      clientName: createProjectLead.name || createProjectLead.company || 'Client',
                      clientEmail: createProjectLead.email,
                      clientPhone: createProjectLead.phone,
                  };
        createProjectMutation.mutate(payload, {
            onSuccess: (project) => {
                const projectId = project?.id ?? project?._id;
                message.success('Project created');
                handleCloseCreateProjectModal();
                // Ensure fresh leads data after project creation
                queryClient.removeQueries({ queryKey: SALES_LEADS_QUERY_KEY });
                queryClient.invalidateQueries({ queryKey: SALES_LEADS_QUERY_KEY });
                // Navigate to project detail page
                if (projectId) {
                    const shellTitle = String(project?.name ?? '').trim();
                    navigate(`/tenant/projects/${projectId}`, {
                        state: shellTitle ? { projectShellTitle: shellTitle } : undefined,
                    });
                }
            },
            onError: (err) => {
                message.error(err?.response?.data?.message ?? 'Failed to create project');
            },
        });
    };

    const handleActionClick = (menuInfo, record) => {
        // if (menuInfo.key === 'edit') {
        //     setSelectedLeadId(record.id);
        //     setPanelOpen(true);
        // }
        if (menuInfo.key === 'invite_client') {
            inviteClientMutation.mutate(record.id);
        }
    };

    const columns = useMemo(
        () => [
            ...(isClientRole
                ? [
                      {
                          title: 'Company',
                          key: 'tenant',
                          width: 220,
                          render: (_, record) => {
                              const name = record.tenantName || '—';
                              const trimmed = (name || '').trim();
                              const initials = trimmed
                                  ? trimmed
                                        .split(/\s+/)
                                        .slice(0, 2)
                                        .map((w) => w[0])
                                        .join('')
                                        .toUpperCase()
                                  : '?';
                              return (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Avatar
                                          size={24}
                                          style={{
                                              backgroundColor: '#1677ff',
                                              fontSize: 11,
                                          }}
                                      >
                                          {initials}
                                      </Avatar>
                                      <span>{name}</span>
                                  </span>
                              );
                          },
                      },
                  ]
                : [
                      { title: 'Client Name', dataIndex: 'name', key: 'name', render: (v) => v || '—' },
                      { title: 'Client Email', dataIndex: 'email', key: 'email', render: (v) => v || '—' },
                      { title: 'Client Phone', dataIndex: 'phone', key: 'phone', render: (v) => v || '—' },
                      { title: 'Client Company', dataIndex: 'company', key: 'company', render: (v) => v || '—' },
                  ]),
            {
                title: 'Source',
                dataIndex: 'source',
                key: 'source',
                width: 120,
                render: (v) => capFirst((v || '').replace(/_/g, ' ')) || '—',
            },
            {
                title: 'Stage',
                dataIndex: 'stage',
                key: 'stage',
                width: 160,
                render: (v, record) =>
                    isClientRole ? (
                        <StatusBadge status={v} size="small" />
                    ) : (
                        <span onClick={(e) => e.stopPropagation()}>
                            <Select
                                $compact
                                value={v && STAGE_OPTIONS.some((o) => o.value === v) ? v : undefined}
                                placeholder="Stage"
                                options={STAGE_OPTIONS}
                                allowClear={false}
                                onChange={(stage) => patchStageMutation.mutate({ leadId: record.id, stage })}
                                loading={stageLoadingId === record.id}
                            />
                        </span>
                    ),
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 160,
                render: (v, record) =>
                    isClientRole ? (
                        <StatusBadge status={v} size="small" />
                    ) : (
                        <span onClick={(e) => e.stopPropagation()}>
                            <Select
                                $compact
                                value={v && STATUS_OPTIONS.some((o) => o.value === v) ? v : undefined}
                                placeholder="Status"
                                options={STATUS_OPTIONS}
                                allowClear={false}
                                onChange={(status) => patchStatusMutation.mutate({ leadId: record.id, status })}
                                loading={statusLoadingId === record.id}
                            />
                        </span>
                    ),
            },
            ...(canUpdate
                ? [
                      {
                          title: 'Action',
                          key: 'action',
                          width: 220,
                          render: (_, record) => {
                              const isClosedWon = (record.stage || '').toLowerCase() === 'closed_won';
                              const hasProject = record.projectId != null;
                              const canCreateProject = isClosedWon && !hasProject;
                              return (
                                  <span
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                                  >
                                      {!record.clientId && !record.isInvited && (
                                          <Button
                                              $primary
                                              $size="12px"
                                              $padding="4px 10px"
                                              loading={inviteLoadingId === record.id}
                                              onClick={() => inviteClientMutation.mutate(record.id)}
                                          >
                                              Invite Client
                                          </Button>
                                      )}
                                      <Button
                                          $primary={false}
                                          $blackOnLight
                                          $size="12px"
                                          $padding="4px 10px"
                                          disabled={!canCreateProject}
                                          onClick={() => {
                                              if (canCreateProject) {
                                                  handleOpenCreateProjectModal(record);
                                              }
                                          }}
                                      >
                                          Create project
                                      </Button>
                                      {/* <Dropdown
                                      menu={{
                                          items: [
                                              { key: 'edit', label: 'Edit' },
                                              { key: 'invite_client', label: 'Invite Client' },
                                          ],
                                          onClick: (info) => handleActionClick(info, record),
                                      }}
                                      trigger={['click']}
                                  >
                                      <span style={{ cursor: 'pointer' }}>
                                          <MoreOutlined />
                                      </span>
                                  </Dropdown> */}
                                  </span>
                              );
                          },
                      },
                  ]
                : []),
        ],
        [
            canUpdate,
            isClientRole,
            stageLoadingId,
            statusLoadingId,
            inviteLoadingId,
            patchStageMutation,
            patchStatusMutation,
            inviteClientMutation,
            handleOpenCreateProjectModal,
        ],
    );

    const openPanel = (id) => {
        setSelectedLeadId(id);
        setPanelOpen(true);
    };
    const closePanel = () => {
        setPanelOpen(false);
        setSelectedLeadId(null);
    };

    const handleOpenCreateLeadModal = () => {
        setCreateLeadModalOpen(true);
    };

    const handleCloseCreateLeadModal = () => {
        setCreateLeadModalOpen(false);
        createLeadForm.resetFields();
    };

    const handleSubmitCreateLead = () => {
        createLeadForm.validateFields().then((values) => {
            const source = values.source ?? 'manual';
            const stage = values.stage ?? 'new';
            const status = values.status ?? 'active';
            const payload = {
                ...(values.name ? { name: String(values.name).trim() } : {}),
                email: String(values.email || '').trim(),
                ...(values.phone ? { phone: String(values.phone).trim() } : {}),
                ...(values.company ? { company: String(values.company).trim() } : {}),
                ...(source ? { source } : {}),
                ...(stage ? { stage } : {}),
                ...(status ? { status } : {}),
                ...(values.estimatedValue != null && values.estimatedValue !== ''
                    ? { estimatedValue: Number(values.estimatedValue) }
                    : {}),
                ...(values.notes ? { notes: String(values.notes).trim() } : {}),
            };

            createLeadMutation.mutate(payload, {
                onSuccess: () => {
                    message.success('Lead created');
                    handleCloseCreateLeadModal();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message ?? 'Failed to create lead');
                },
            });
        });
    };

    return {
        // data
        leads,
        total,
        activeCount,
        qualifiedCount,
        closedCount,
        filteredLeads,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        // panel
        panelOpen,
        setPanelOpen,
        selectedLeadId,
        setSelectedLeadId,
        selectedLeadName,
        openPanel,
        closePanel,
        // permissions
        canView,
        canCreate,
        canUpdate,
        canDelete,
        // mutations
        createLeadMutation,
        updateLeadMutation,
        deleteLeadMutation,
        // error state
        leadsError,
        // helpers
        capFirst,
        // create lead
        createLeadModalOpen,
        setCreateLeadModalOpen,
        createLeadForm,
        handleOpenCreateLeadModal,
        handleCloseCreateLeadModal,
        handleSubmitCreateLead,
        // create project from lead
        createProjectModalOpen,
        createProjectLead,
        handleCloseCreateProjectModal,
        handleCreateProjectFromLead,
        createProjectMutation,
    };
};
